import React, { useState } from 'react';
import {
  Card,
  Title,
  Text,
  TabGroup,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  AreaChart,
  Legend,
  Flex,
  Callout,
  Bold,
  LineChart,
  Color
} from '@tremor/react';
import { InformationCircleIcon } from '@heroicons/react/24/solid';

// Calculate compounded growth over time
const calculateGrowth = (
  initialAmount: number,
  annualContribution: number,
  years: number,
  rate: number,
  taxRate: number,
  accountType: 'TFSA' | 'RRSP'
) => {
  // RRSP gets tax deduction up front so can invest more initially
  const effectiveInitialAmount = accountType === 'RRSP' 
    ? initialAmount / (1 - taxRate) 
    : initialAmount;
  
  const yearlyData = [];
  let currentAmount = effectiveInitialAmount;
  
  for (let year = 1; year <= years; year++) {
    // Add annual contribution
    currentAmount += annualContribution;
    
    // Apply growth rate
    currentAmount *= (1 + rate);
    
    // For RRSP, we need to consider the tax on withdrawal
    const afterTaxAmount = accountType === 'RRSP' 
      ? currentAmount * (1 - taxRate) 
      : currentAmount;
    
    yearlyData.push({
      year,
      [accountType]: Math.round(currentAmount),
      [`${accountType}_AfterTax`]: Math.round(afterTaxAmount)
    });
  }
  
  return yearlyData;
};

// Merge data sets
const mergeDataSets = (tfsaData: any[], rrspData: any[]) => {
  return tfsaData.map((tfsa, index) => ({
    year: tfsa.year,
    TFSA: tfsa.TFSA,
    TFSA_AfterTax: tfsa.TFSA_AfterTax,
    RRSP: rrspData[index].RRSP,
    RRSP_AfterTax: rrspData[index].RRSP_AfterTax
  }));
};

interface InvestmentComparisonChartProps {
  title?: string;
  description?: string;
}

export default function InvestmentComparisonChart({ 
  title = "TFSA vs. RRSP: Long-term Investment Comparison", 
  description = "Compare how $10,000 initial investment with $5,000 annual contributions grows over 30 years"
}: InvestmentComparisonChartProps) {
  const [initialAmount] = useState(10000);
  const [annualContribution] = useState(5000);
  const [years] = useState(30);
  const [rate] = useState(0.07); // 7% annual return
  const [selectedTaxScenario, setSelectedTaxScenario] = useState(0);
  
  // Different tax scenarios
  const taxScenarios = [
    { name: "Low Now, High Later", currentTaxRate: 0.20, futureTaxRate: 0.30 },
    { name: "Same Tax Rate", currentTaxRate: 0.30, futureTaxRate: 0.30 },
    { name: "High Now, Low Later", currentTaxRate: 0.40, futureTaxRate: 0.25 }
  ];
  
  const currentScenario = taxScenarios[selectedTaxScenario];
  
  // Generate data
  const tfsaData = calculateGrowth(
    initialAmount,
    annualContribution,
    years,
    rate,
    currentScenario.futureTaxRate,
    'TFSA'
  );
  
  const rrspData = calculateGrowth(
    initialAmount,
    annualContribution,
    years,
    rate,
    currentScenario.futureTaxRate,
    'RRSP'
  );
  
  const mergedData = mergeDataSets(tfsaData, rrspData);
  
  // Get final values for display
  const finalTfsa = tfsaData[tfsaData.length - 1].TFSA_AfterTax;
  const finalRrsp = rrspData[rrspData.length - 1].RRSP_AfterTax;
  const difference = Math.abs(finalTfsa - finalRrsp);
  const winner = finalTfsa > finalRrsp ? 'TFSA' : 'RRSP';
  
  // Format as currency
  const formatter = (value: number) => 
    `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  
  return (
    <Card className="mt-4 mb-8 bg-white border border-gray-200 shadow-md hover:shadow-lg transition-all rounded-lg">
      <Title className="text-gray-900">{title}</Title>
      <Text className="text-gray-700">{description}</Text>
      
      <TabGroup 
        index={selectedTaxScenario}
        onIndexChange={setSelectedTaxScenario}
        className="mt-6"
      >
        <TabList variant="solid" color="blue">
          {taxScenarios.map((scenario, index) => (
            <Tab key={index}>{scenario.name}</Tab>
          ))}
        </TabList>
        
        <TabPanels>
          {taxScenarios.map((scenario, index) => (
            <TabPanel key={index}>
              <div className="mt-4">
                <Callout 
                  title={`Tax Scenario: ${scenario.name}`}
                  icon={InformationCircleIcon}
                  color="blue"
                  className="mt-4 mb-4 border border-blue-200 bg-blue-50"
                >
                  <Text>
                    Current Tax Rate: <Bold>{(scenario.currentTaxRate * 100).toFixed(0)}%</Bold> | 
                    Future Tax Rate: <Bold>{(scenario.futureTaxRate * 100).toFixed(0)}%</Bold>
                  </Text>
                </Callout>
                
                <Legend
                  className="justify-center mb-3"
                  categories={["TFSA (After Tax)", "RRSP (After Tax)"]}
                  colors={["blue", "green"]}
                />
                
                <AreaChart
                  className="h-80 mt-4"
                  data={mergedData}
                  index="year"
                  categories={["TFSA_AfterTax", "RRSP_AfterTax"]}
                  colors={["blue", "green"]}
                  valueFormatter={formatter}
                  showLegend={false}
                  yAxisWidth={80}
                  showAnimation={true}
                />
                
                <Flex justifyContent="between" className="mt-6">
                  <Card decoration="top" decorationColor="blue" className="w-1/2 mr-2 bg-white border border-gray-200 shadow-sm">
                    <Text className="text-gray-700">TFSA Final Value (After Tax)</Text>
                    <Bold className="text-xl text-blue-700">{formatter(finalTfsa)}</Bold>
                  </Card>
                  <Card decoration="top" decorationColor="green" className="w-1/2 ml-2 bg-white border border-gray-200 shadow-sm">
                    <Text className="text-gray-700">RRSP Final Value (After Tax)</Text>
                    <Bold className="text-xl text-green-700">{formatter(finalRrsp)}</Bold>
                  </Card>
                </Flex>
                
                <Card className="mt-4 bg-gray-50 border border-gray-200 shadow-sm">
                  <Text className="text-gray-800">In this scenario, <Bold className="text-lg text-blue-800">{winner}</Bold> performs better by</Text>
                  <Bold className="text-xl text-brand-primary">{formatter(difference)}</Bold>
                  <Text className="mt-2 text-xs text-gray-600">
                    Based on {years} years of growth with {(rate * 100).toFixed(0)}% annual returns,
                    ${initialAmount.toLocaleString()} initial investment, and 
                    ${annualContribution.toLocaleString()} annual contributions.
                  </Text>
                </Card>
              </div>
            </TabPanel>
          ))}
        </TabPanels>
      </TabGroup>
    </Card>
  );
}
